import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ArrowLeft, Store as StoreIcon, MapPin, MessageCircle, Truck, Globe, Clock } from "lucide-react";

export default function StorePublic() {
  const { tiendaId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/tiendas/${tiendaId}/publica`).then(r => setData(r.data)).catch(() => setError(true));
  }, [tiendaId]);

  if (error) return <div className="min-h-screen bg-[#F9F9F8]"><Navbar /><div className="p-16 text-center text-zinc-500">Tienda no encontrada</div></div>;
  if (!data) return <div className="min-h-screen bg-[#F9F9F8]"><Navbar /><div className="p-16 text-center text-zinc-500">Cargando…</div></div>;

  const { tienda, productos } = data;
  const wa = (tienda.whatsapp_gerente || tienda.telefono || "").replace(/\D/g, "");

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="store-public-page">
      <Navbar />
      <div className="bg-zinc-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid md:grid-cols-[220px_1fr] gap-8 items-center">
          <div className="aspect-square w-40 md:w-full bg-zinc-800 rounded-sm overflow-hidden grid place-items-center">
            {tienda.imagen_url ? <img src={tienda.imagen_url} alt={tienda.nombre_local} className="w-full h-full object-cover" /> : <StoreIcon className="w-12 h-12 text-zinc-600" />}
          </div>
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white" data-testid="store-back-home"><ArrowLeft className="w-3.5 h-3.5" /> Volver al e-commerce general</Link>
            <div className="overline text-[#D4FF00] mt-4">Tienda online · {tienda.categoria}</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1" data-testid="store-name">{tienda.nombre_local}</h1>
            {tienda.descripcion && <p className="text-white/70 mt-3 max-w-xl text-sm md:text-base">{tienda.descripcion}</p>}
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {(tienda.ciudad || tienda.estado) && (
                <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-sm"><MapPin className="w-3.5 h-3.5" /> {[tienda.colonia, tienda.ciudad, tienda.estado].filter(Boolean).join(", ")}</span>
              )}
              {tienda.horario_apertura && <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-sm"><Clock className="w-3.5 h-3.5" /> {tienda.horario_apertura} – {tienda.horario_cierre}</span>}
              <span className="inline-flex items-center gap-1.5 bg-[#FF3366] px-3 py-1.5 rounded-sm font-medium"><Truck className="w-3.5 h-3.5" /> Entrega local en 90 min</span>
              {tienda.envia_uber && <span data-testid="badge-uber" className="inline-flex items-center gap-1.5 bg-white text-zinc-900 px-3 py-1.5 rounded-sm font-medium"><Truck className="w-3.5 h-3.5" /> Enviamos por Uber</span>}
              {tienda.envia_foraneo && <span data-testid="badge-foraneo" className="inline-flex items-center gap-1.5 bg-[#D4FF00] text-zinc-900 px-3 py-1.5 rounded-sm font-medium"><Globe className="w-3.5 h-3.5" /> Envíos foráneos · hasta 72 h</span>}
            </div>
            {wa && (
              <a href={`https://wa.me/${wa.length === 10 ? "52" + wa : wa}`} target="_blank" rel="noreferrer" data-testid="store-whatsapp" className="mt-5 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-sm text-sm font-medium">
                <MessageCircle className="w-4 h-4" /> WhatsApp de la tienda
              </a>
            )}
          </div>
        </div>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display font-bold text-2xl lg:text-3xl tracking-tighter">Catálogo <span className="text-zinc-400 text-xl ml-2" data-testid="store-product-count">{productos.length}/30</span></h2>
        </div>
        {productos.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">Esta tienda aún no publica artículos.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {productos.map(p => {
              const agotado = !p.estado_disponible || p.stock_disponible === 0;
              return (
                <div key={p.id} data-testid={`store-product-${p.id}`} className="card-lift bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
                  <Link to={`/producto/${p.id}`} className="aspect-square bg-zinc-100 block overflow-hidden relative">
                    {p.imagen_url ? <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-400 text-sm">Sin imagen</div>}
                    {agotado && <div className="absolute inset-0 bg-black/60 grid place-items-center text-white font-display font-bold">AGOTADO</div>}
                  </Link>
                  <div className="p-3 flex flex-col flex-1">
                    <div className="overline text-zinc-400">{p.categoria}</div>
                    <Link to={`/producto/${p.id}`} className="font-display font-semibold tracking-tight line-clamp-1 hover:text-[#FF3366]">{p.nombre}</Link>
                    <div className="flex items-end justify-between mt-2">
                      <span className="font-display font-black text-lg">${p.precio.toFixed(2)}</span>
                      <Button data-testid={`store-add-${p.id}`} size="sm" disabled={agotado} onClick={() => { if (add(p) !== false) toast.success(`${p.nombre} agregado`); }} className="rounded-sm bg-zinc-900 hover:bg-[#FF3366] h-8 px-3">+</Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
